import { Container, Point, Loader, LoaderResource } from "pixi.js";
import { ScreenContainer } from "./ScreenContainer";
import { ScreenConfig } from "./ScreenConfig";
import { Screen } from "./Screen";
import { PixiApp } from "../app/PixiApp";
import { UiContainer } from "../ui/UiContainer";
import { LoaderDisplay } from "../loader/LoaderDisplay";
import { AssetLoaderEvents } from "../loader";
import { Tween } from "../animation/Tween";

/**
 * PixiJS screen manager class.
 */
export class ScreenManager extends Container {
  /**
   * Anchor point of screen.
   */
  public screenAnchor: Point = new Point(0.5, 0.5);

  /**
   * Array of screen added screen configurations.
   */
  public screens: ScreenConfig[] = [];

  /**
   */
  public history: string[] = [];

  /**
   * Screen instance currently being shown.
   */
  public current: Screen;

  /**
   * PIXI.Application instance.
   */
  public app: PixiApp;

  /**
   * Enable debug logging.
   */
  public debug?: boolean = false;

  /**
   * Top level container.
   */
  private container: ScreenContainer;

  /**
   * UI layer.
   */
  private ui: UiContainer;

  /**
   * Top layer.
   */
  private top: Container;

  /**
   * Constructor class for the loding screen display, must
   * extend LoaderDisplay.
   */
  private loaderDisplayCtor: typeof LoaderDisplay = LoaderDisplay;

  /**
   * LoaderDisplay instance.
   */
  private loaderDisplay: LoaderDisplay;

  /**
   * Create an instance of PixiScreenManager.
   *
   * @param app - PIXI.Application instance.
   */
  constructor(app: PixiApp) {
    super();
    // PIXI.Application instance reference.
    this.app = app;

    // Create top level container and add it to app stage.
    this.container = new ScreenContainer();
    this.addChild(this.container);

    // Create UI Layer.
    this.ui = new UiContainer();
    this.addChild(this.ui);

    // Create top layer.
    this.top = new UiContainer();
    this.addChild(this.top);

    //
    this.app.stage.addChild(this);

    // Add events.
    this.app.ticker.add(this.update, this);
  }

  /**
   * Update the manager.
   * @param dt
   */
  private update(dt: number) {
    if (this.current) {
      this.current.callUpdate(dt);
      this.current.callPostUpdate(dt);
    }
  }

  /**
   * Screen manager resize listener.
   * @param width
   * @param height
   */
  public resize(width: number, height: number) {
    this.container.pivot.set(
      Math.round(-width * this.screenAnchor.x),
      Math.round(-height * this.screenAnchor.y)
    );
    this.ui.pivot.set(
      Math.round(-width * this.screenAnchor.x),
      Math.round(-height * this.screenAnchor.y)
    );
    this.top.pivot.set(
      Math.round(-width * this.screenAnchor.x),
      Math.round(-height * this.screenAnchor.y)
    );

    for (const screen of this.screens)
      if (screen.instance) {
        screen.instance.callResize(width, height);
      }
  }

  /**
   * Add an array of screens to the manager.
   * @param screens
   * @param show
   */
  public addScreens(screens: ScreenConfig[]) {
    if (this.debug) console.groupCollapsed("ScreenManager.addScreens");
    for (let screen of screens) this.addScreen(screen);
    if (this.debug) console.groupEnd();
  }

  /**
   * Add a screen to the manager.
   * @param screen
   * @param show
   */
  public addScreen(screen: ScreenConfig) {
    if (this.debug) console.log(`ScreenManager - Add screen: ${screen.name}`);
    this.screens.push(screen);
    if (screen.instanceOnAdd) screen.instance = this.getScreenInstance(screen);
  }

  /**
   * Set a screen instances visibility.
   * @param visible
   */
  public setScreenVisible(screen: Screen, visible: boolean) {
    if (visible !== screen.visible) {
      if (this.debug) console.log(`ScreenManager - Set visible (${visible}): ${screen.name}`);
      screen.ui.visible = visible;
      screen.visible = visible;
    }
  }

  /**
   * Get a screen with given name.
   * @param name
   */
  public getScreenConfig(name: string): ScreenConfig | undefined {
    let foundScreen;
    for (let screen of this.screens) if (screen.name === name) foundScreen = screen;
    return foundScreen;
  }

  /**
   * Destroy a screen instance.
   * @param screen
   */
  private destroyScreen(screen: ScreenConfig) {
    if (screen.instance) {
      if (this.debug) console.log(`ScreenManager - Destroying screen: ${screen.name}`);
      screen.instance.destroy({ children: true, texture: false });
      delete screen.instance;
    }
  }

  /**
   * Hide a screen.
   * @param screen
   */
  private hideScreen(screen: ScreenConfig) {
    if (screen.instance) {
      if (!screen.keepAlive) this.destroyScreen(screen);
      else this.setScreenVisible(screen.instance, false);
    }
  }

  /**
   * Hide all screens.
   */
  private hideAllScreens() {
    for (let screen of this.screens) this.hideScreen(screen);
  }

  /**
   * Show a screen.
   */
  private currentScreenName: string;
  public showScreen(name: string, tags?: any, screenState?: any) {
    const screen = this.getScreenConfig(name);
    if (!screen) {
      console.warn(`Screen Manager - Screen: ${name} not found.`);
      return;
    }

    Tween.removeAllTweens();

    this.currentScreenName = name;

    let allScreenTags = screen.assetTags.concat(tags);

    if (!this.app.assets.checkTagsNeedToLoad(allScreenTags)) {
      if (this.debug) console.log(`ScreenManager - Showing screen: ${name}`);
      this.hideAllScreens();
      screen.instance = this.getScreenInstance(screen);
      this.startScreen(screen.instance);
      this.setScreenVisible(screen.instance, true);
      this.current = screen.instance;
      this.history.unshift(screen.name);
    } else {
      if (this.loaderDisplay) this.loaderDisplay.destroy();
      this.loaderDisplay = new this.loaderDisplayCtor();
      this.app.assets.on(AssetLoaderEvents.PROGRESS, this.onLoadProgress, this);
      this.app.assets.once(AssetLoaderEvents.COMPLETE, this.onLoadComplete, this);
      this.loaderDisplay.transitionIn(() => {
        this.hideAllScreens();
        this.app.loader.reset();
        this.app.assets.addAssetsWithTags(allScreenTags);
        this.app.loader.load();
      });
      this.top.addChild(this.loaderDisplay);
    }
  }

  private onLoadProgress(loader: Loader, resource: LoaderResource) {
    this.loaderDisplay.onProgress(loader, resource);
  }

  private onLoadComplete(loader: Loader, resource: LoaderResource) {
    if (this.loaderDisplay.autoProgress) {
      this.loaderDisplay.onComplete(loader, resource);
      this.next();
    } else {
      this.loaderDisplay.onComplete(loader, resource);
      this.loaderDisplay.on("continue", () => this.next());
    }
    this.app.assets.off(AssetLoaderEvents.PROGRESS);
    this.app.assets.off(AssetLoaderEvents.COMPLETE);
  }

  private next() {
    this.showScreen(this.currentScreenName);
    this.loaderDisplay.transitionOut(() => {
      if (this.loaderDisplay) this.loaderDisplay.destroy();
    });
  }

  public setLoaderDisplayCtor(ctor: typeof LoaderDisplay) {
    this.loaderDisplayCtor = ctor;
  }

  /**
   * Get a screen instance, or if it doesn't exist, create one.
   * @param screen
   */
  private getScreenInstance(screen: ScreenConfig): Screen {
    let instance = screen.instance;
    if (!instance) {
      instance = new screen.class(this.app);
      instance.name = screen.name;
      this.initScreen(instance);
    }
    return instance;
  }

  /**
   * Initialize a screen. Called on instancing a screen.
   */
  private initScreen(screen: Screen) {
    if (this.debug) console.log(`ScreenManager - Init screen: ${screen.name}`);
    if (screen) {
      screen.callInit(screen.screenState);
      this.container.addChild(screen);
      this.ui.addChild(screen.ui);
    }
  }

  /**
   * Start a screen. Called on showing a screen.
   * @param screen
   */
  private startScreen(screen: Screen) {
    if (this.debug) console.log(`ScreenManager - Start screen: ${screen.name}`);
    screen.callResize(this.app.resizer.width, this.app.resizer.height);
    screen.callStart();
    screen.callTransitionIn();
  }

  /**
   * Navigates to the appropriate screen, for the specified button, as defined in screens.js.
   * @param buttonName - Name of the button which is being used to navigate.
   */
  public navigate(buttonName: string) {
    const screen = this.getScreenConfig(this.current.name);
    if (screen.navigation) {
      for (const key in screen.navigation) {
        if (key === buttonName) this.app.screens.showScreen(screen.navigation[key]);
      }
    }
  }
}
